import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGroups } from "@/lib/api/group";
import { useRemoveUserRole, useUserRoles } from "@/lib/api/roles";
import type { UserRoleAssignment } from "@/lib/api/roles";
import { useAbility } from "@/lib/casl/ability-context";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { AssignRoleDialog } from "./assign-role-dialog";
import { EditScopeDialog } from "./edit-scope-dialog";

interface RolesTabProps {
  userId: string;
}

export function RolesTab({ userId }: RolesTabProps) {
  const { data: userRoles, isLoading } = useUserRoles(userId);
  const { data: groups } = useGroups(false);
  const removeRole = useRemoveUserRole(userId);
  const ability = useAbility();
  const canAssignRoles = ability.can("assign", "roles");
  const canManageAll = ability.can("manage", "all");

  const [showAssign, setShowAssign] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const [editScopeTarget, setEditScopeTarget] =
    useState<UserRoleAssignment | null>(null);

  const getGroupName = (groupId: string) => {
    return groups?.find((g) => g.id === groupId)?.name || groupId;
  };

  const isLastRole = (userRoles?.length ?? 0) <= 1;

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Assigned Roles</h3>
        {canAssignRoles && (
          <Button size="sm" onClick={() => setShowAssign(true)}>
            <Plus className="mr-2 size-4" />
            Assign Role
          </Button>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Role</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Group Scope</TableHead>
            <TableHead>Assigned</TableHead>
            {canAssignRoles && <TableHead className="text-right">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {userRoles?.map((ur) => (
            <TableRow key={ur.id}>
              <TableCell className="font-medium">{ur.role.name}</TableCell>
              <TableCell>
                {ur.role.is_system ? (
                  <Badge variant="secondary">System</Badge>
                ) : (
                  <Badge variant="outline">Custom</Badge>
                )}
              </TableCell>
              <TableCell>
                {ur.scope?.groups?.length ? (
                  <div className="flex flex-wrap gap-1">
                    {ur.scope.groups.map((groupId) => (
                      <Badge
                        key={groupId}
                        variant="outline"
                        className="text-xs"
                      >
                        {getGroupName(groupId)}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">Unscoped</span>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {new Date(ur.granted_at).toLocaleDateString()}
              </TableCell>
              {canAssignRoles && (
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditScopeTarget(ur)}
                      title="Edit group scope"
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={
                        isLastRole ||
                        (!canManageAll && ur.role.name === "superadmin")
                      }
                      onClick={() => setRemoveTarget(ur.role_id)}
                      title={
                        isLastRole
                          ? "Cannot remove the last role"
                          : !canManageAll && ur.role.name === "superadmin"
                            ? "Only superadmins can remove this role"
                            : "Remove role"
                      }
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
          {!userRoles?.length && (
            <TableRow>
              <TableCell
                colSpan={canAssignRoles ? 5 : 4}
                className="text-center text-muted-foreground"
              >
                No roles assigned
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <AssignRoleDialog
        userId={userId}
        existingRoleIds={userRoles?.map((ur) => ur.role_id) ?? []}
        open={showAssign}
        onOpenChange={setShowAssign}
      />

      {editScopeTarget && (
        <EditScopeDialog
          userId={userId}
          roleId={editScopeTarget.role_id}
          roleName={editScopeTarget.role.name}
          currentScope={editScopeTarget.scope}
          open={!!editScopeTarget}
          onOpenChange={(open) => {
            if (!open) setEditScopeTarget(null);
          }}
        />
      )}

      <ConfirmActionDialog
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        title="Remove Role"
        description="Are you sure you want to remove this role from the user? They will lose all permissions associated with it."
        onConfirm={() => {
          if (removeTarget) {
            removeRole.mutate(removeTarget, {
              onSuccess: () => setRemoveTarget(null),
            });
          }
        }}
        loading={removeRole.isPending}
      />
    </div>
  );
}
